import { cva, type VariantProps } from 'class-variance-authority';

export { CharlieButton } from './charlie-button.js';

export interface CharlieButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The label of the button. Convenience alternative to `children`.
   */
  label?: string;
  /**
   * The visual variant of the button.
   */
  variant?: CharlieButtonVariants['variant'];
  /**
   * The size of the button.
   */
  size?: CharlieButtonVariants['size'];
  /**
   * Whether the button should be a pill (fully rounded).
   */
  pill?: boolean;
  /**
   * Whether the button should be full-width.
   */
  block?: boolean;
  /**
   * Whether the button should show a loading spinner.
   */
  loading?: boolean;
  /**
   * Render as a child element (uses Radix Slot). Pass a single child element
   * and the button's props/classes will be merged onto it.
   */
  asChild?: boolean;
}

export const charlieButtonVariants = cva(
  "inline-flex select-none font-inter items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent text-semi-dark-grey hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary-hover',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3 text-sm',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 text-sm',
        lg: 'h-12 rounded-md px-6 has-[>svg]:px-4 text-base',
        icon: 'size-9 text-sm',
      },
      pill: {
        true: 'rounded-full',
      },
      block: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type CharlieButtonVariants = VariantProps<typeof charlieButtonVariants>;
